import 'core/utils/ArrayUtils';
import { Bus, Localize, handleServerErrors, LicenseChecker, Events, Log, Level, Result, SessionProvider, VerifactuIssueChecker, Telemetry } from 'core/infrastructure';
import { Command, Electron, Platform, LogAppender, Cordova } from './common';
import { HelpProvider, ProcessDialog, ThemeManager } from './ui';
import configureSettings from './modules/main/login/configureSettings';
import { Res, MachineType, FiscalSystem, PosType } from 'core/api';
import { GlobalErrorHandling } from 'core/infrastructure-next';
import { Environment, FastClick } from 'core/utils';
import './registerCommands';
import { RestaurantFacade } from './facades';
import { SettingsProvider, CustomerDisplay, UserActivityMonitor, CashControl, LoginService, NativeLogUploader, CertificateExpirationNotification } from './services';
import * as React from 'react';
import ReactDOM from 'react-dom/client';
import { Message, Modal, ModalUtils } from 'core/ui-next/modal';
import { Theme } from './ui';
import { Provider, Toaster } from 'core/ui-next/chakra-components';
import { connectionCheckListener, processOutOfBandMessages } from 'core/ui-next';
import { ErrorBoundary } from 'core/infrastructure-next/GlobalErrorHandling';


// Cuanto antes mejor, pero además podemos asumir que ya estamos dentro del entorno de chakra. Ya que startApp que realiza todas las operaciones previas es ejecutado desde
// Modal.Manager que se encuentra ya dentro de chakra.
GlobalErrorHandling.configure({
	onErrorAction: Electron.isEnabled() || Cordova.isCordovaNavigator() ? 'close' : 'reload',
	onLicenseInvalid: async (errorMessage: string) => {

		// Si soy no soy electron y no tengo licencia, borro los settings por si
		// se han equivocado al elegir el tpv y han puesto un punto de venta en
		// lugar de una comandera. Si no, para poder arreglarlo hay que borrar datos
		// de aplicación, y eso no es fácil en el caso del APK y menos de los
		// datáfonos de Adyen. Lo hago antes de sacar el mensaje para que dé
		// tiempo a que se haga el flush de LocalStorage antes de cerrar el APK
		if (!Electron.isEnabled()) {
			SettingsProvider.clear();
		}

		await Message.show(errorMessage);

		if (Electron.isEnabled()) {
			void Electron.Window.exitApp();
		}
		else if (Cordova.isCordovaNavigator()) {
			await Cordova.exitApp();
		}
		else {
			window.location.reload();
		}
	}
});

// Se deshabilitan todos los menús contextuales porque en Android
// al auto-seleccionar los textos de los inputs, se muestra
// por defecto un menú del sistema.
(function disableContextMenu() {
	if (!Environment.isMobile())
		return;

	window.oncontextmenu = function (event: Event) {
		event.preventDefault();
		event.stopPropagation();
		return false;
	};
})();

// Deshabilito el botón atrás del navegador
(function hijackBackButton() {

	let lastEventTimeStamp = 0;

	history.pushState(null, '', location.href);
	window.onpopstate = function (ev: PopStateEvent) {
		history.go(1);

		// Plan: Genero un evento que puedan recibir los screen y diálogos para cerrarse
		// o hacer lo que les dé la gana en respuesta al back del navegador.
		// El evento onpopstate se lanza 2 veces, por lo que hago un debounce para evitar
		// que llege dos veces nuestro evento a las pantallas.
		// Para evitar que lo procesen pantallas que no están en primer plano, paso el
		// #id del nodo HTML que está en primer plano. No puedo basarme simplementemente
		// en el isInForeground de cada PosScreenClassComponent porque como se van cerrando en respuesta
		// al evento, según se cierra una la anterior pasa a estar en primer plano y se
		// acabarían cerrando todas.
		if (ev.timeStamp - lastEventTimeStamp > 500) {

			const viewsIds = ModalUtils.getAllIds();

			if (viewsIds.length) {
				const viewId = viewsIds[viewsIds.length - 1];
				void Events.raise('browser:back', { viewId });
			}
		}

		lastEventTimeStamp = ev.timeStamp;
	};
})();

(function configureFastClick() {
	FastClick.attach(document.body, {
		tapDelay: 20,
		touchBoundary: 50,
	});
})();

// Configuro el select2
(function configureSelect2() {
	$.fn.select2.defaults.set('theme', 'bootstrap');
})();

(function loadSettingsFromUrl() {
	// Si recibo configuración desde la URL, la actualizo y recargo para que surtan efecto los cambios

	const POS_ID_PARAM = 'posId';
	const THEME_PARAM = 'theme';
	const LANGUAGE_PARAM = 'lang';
	const LOG_LEVEL_PARAM = 'logLevel';


	const url = new URL(document.location.toString());
	// Nos protegemos de webviews antiguos que no tienen searchparams
	if (url.searchParams && url.searchParams.has) {

		const settings = SettingsProvider.load();
		if (url.searchParams.has(POS_ID_PARAM)) {
			const posId = parseInt(url.searchParams.get(POS_ID_PARAM) || '');
			settings.posId = posId;
		}

		if (url.searchParams.has(THEME_PARAM)) {
			const theme = url.searchParams.get(THEME_PARAM) || 'light';
			settings.theme = theme;
		}

		if (url.searchParams.has(LANGUAGE_PARAM)) {
			const language = url.searchParams.get(LANGUAGE_PARAM) || 'es';
			settings.languageCode = language;
		}

		if (url.searchParams.has(LOG_LEVEL_PARAM)) {
			const defaultLevel = url.searchParams.get(LOG_LEVEL_PARAM) === '' ? `${Level.INFO}` : url.searchParams.get(LOG_LEVEL_PARAM)!;
			const level = parseInt(defaultLevel, 10);
			settings.logLevel = level;
		}

		SettingsProvider.save(settings);

		// Recargo sin parámetros
		url.search = '';
		history.replaceState(null, document.title, url.toString());
	}
})();

async function configureApp(): Promise<Result> {

	// Necesito inicializar el bus aunque no tenga unos settings válidos para poder
	// hablar con el servidor y bajarme la lista de TPVs, idiomas, etc., que hacen
	// falta para la pantalla de configuración.
	//
	// En el peor de los casos, si la configuración no era válida mandaremos al
	// usuario a la pantalla de config, pondrá una configuración y recargaremos
	// la página, con lo que eventualmente esta configuración del bus será correcta.
	//
	// Una vez que tenemos posId válido, se hablará con el servidor para obtener el
	// DeviceConfiguration y se actualizará la configuración del bus con los valores
	// reales de applicationName y machineType.

	const settings = SettingsProvider.load();

	Bus.init({
		applicationName: 'Unknown',
		machineId: settings.machineId,
		posId: settings.posId,
		machineType: MachineType.SmartphoneOrTablet,
		connectionCheckListener: connectionCheckListener,
		outOfBandMessageProcessor: processOutOfBandMessages
	});

	if (!settings.posId || settings.posId === 0) {
		await configureSettings();
		return Result.error();
	}

	Log.init(settings.logLevel, LogAppender.create(settings));

	Localize.setDefaultLanguageCode(settings.languageCode);
	await Localize.setLanguageCode(settings.languageCode, async (languageCode: string) => await Events.raise('language:changed', languageCode));
	ThemeManager.activateTheme(settings.theme);

	const result = await handleServerErrors(async () => {

		Log.warn('\t>>>> Cargando aplicación web <<<<');
		Log.warn(`\tUrl: ${window.location.href}`);
		Log.warn(`\tWebAppVersion: ${AGORA_VERSION}`);
		Log.warn(`\tUserAgent: ${navigator.userAgent}`);
		Log.warn(`\tMachineId: ${settings.machineId}`);
		Log.warn(`\tPosId: ${settings.posId}`);
		Log.warn(`\tLanguageCode: ${settings.languageCode}`);
		Log.warn(`\tLogLevel: ${settings.logLevel}`);
		Log.warn(`\tTheme: ${settings.theme}`);

		const configuration = await RestaurantFacade.getDeviceConfiguration();
		Bus.updateConfig({
			applicationName: configuration.ApplicationName,
			machineType: configuration.MachineType
		});

		SessionProvider.setAttendanceModuleEnabled(configuration.EnableAttendanceModule);

		CertificateExpirationNotification.init(configuration);
		if (configuration.FiscalSystem === FiscalSystem.Verifactu) {
			await VerifactuIssueChecker.startChecking({
				AdminHttpPort: configuration.AdminHttpPort,
				AdminHttpUseSsl: configuration.AdminHttpUseSsl,
				HubUrl: configuration.HubUrl
			});
		}

		ThemeManager.setButtonSize(configuration.DisplayProfile.ButtonSize);
		ThemeManager.setDeviceType(configuration.Type);
		ThemeManager.setScrollMode(configuration.DisplayProfile.ScreenScrollMode);

		const validationResult = Platform.validate(configuration);
		if (!validationResult.isValid) {
			await Message.show(Res.Msg.MSG_PLATFORM_DOES_NOT_SUPPORT_FEATURES(settings.posId, validationResult.errors.map(x => `- ${x}`).join('\r\n')));
			settings.posId = 0;
			SettingsProvider.save(settings);
			window.location.reload();
			return;
		}

		await Telemetry.start({
			platform: Platform.getType(),
			application: configuration.Type === PosType.POS ? 'Pos' : configuration.Type === PosType.TABLET ? 'Tablet' : 'Smartphone',
			machineId: settings.machineId
		}, RestaurantFacade.getTelemetrySettings);

		await ProcessDialog.showWhile({
			title: Res.Msg.MSG_INITIALIZATION_DEVICE_DIALOG_TITLE(),
			msg: Res.Msg.MSG_INITIALIZATION_DEVICE_DIALOG_CONTENT(),
			delay: 1000,
			action: async () => await Platform.configure(configuration, settings, CashControl.startListenPendingActions)
		});

		// Hay veces que uno se sale de la aplicación sin hacer un logout y nos estarían llegando notificaciones en la pantalla de usuario sin hacer login
		await LoginService.unregisterNotificationToken();

		// No lo configuramos desde Platform para evitar dependencia circular entre common <-> services
		await CustomerDisplay.configure(configuration.CustomerDisplayConfiguration);

		// Hasta que no sé que existe el pos no empiezo a validar la licencia.
		if (!(await Platform.shouldIgnoreLicenseCheck()))
			await LicenseChecker.startChecking(settings.machineId);

		UserActivityMonitor.start();
		NativeLogUploader.start();
	}, {

		onDomainError: async _error => {
			// Si hay algún error al obtener la configuración seguramente sea porque no existe el punto de venta
			// Limpiamos los settings y volvemos a lanzar la aplicación
			SettingsProvider.clear();
		}
	});

	return result;

}

async function startApp() {

	const result = await configureApp();
	if (!result.ok) {
		// Si no he conseguido configurar la aplicación, recargo y pruebo suerte la próxima vez
		window.location.reload();
		return;
	}

	await Command.execute('login');
}

function getColorModeTheme() {
	const settings = SettingsProvider.load();
	return settings.theme;
}


const AppProvider = ({ children }: React.PropsWithChildren) =>
	<Provider theme={Theme} enableSystem={false} defaultTheme={getColorModeTheme()}>
		<ErrorBoundary>
			<HelpProvider>
				<Toaster.Manager />
				<Modal.ModalProvider>
					{children}
				</Modal.ModalProvider>
			</HelpProvider>
		</ErrorBoundary>
	</Provider>;


ReactDOM.createRoot(document.getElementById('app')!).render(
	<React.StrictMode>
		<AppProvider>
			<Modal.Manager onReady={() => startApp()} />
		</AppProvider>
	</React.StrictMode>
);

